import { AppThunkAction } from "..";
import { toBase64 } from "../../Utility";
import { KnownAction } from "./actionTypes";
import {
  AwayModeSettings,
  CreateUWG5NventRequest,
  DateTimeSettings,
  DeviceInfo,
  DisplaySettings,
  Errors,
  FloorSensorSettings,
  HeatingSettings,
  RegulationSettings,
  UWG5NventSettings,
  WarrantyInfo,
  WifiSettings,
} from "./models";
import { uwg5NventHub } from "./signalRHub";

export enum UWG5NventAction {
  CreateNew = "CreateNew",
  Remove = "RemoveThermostat",
  Connect = "Connect",
  Link = "Link",
  Unlink = "Unlink",
  Disconnect = "Disconnect",
  IncreaseSetpoint = "IncreaseSetpoint",
  DecreaseSetpoint = "DecreaseSetpoint",
  Save = "SaveThermostat",
  ToggleTimer = "ToggleTimer",
  ToggleHeatRelay = "ToggleHeatRelay",
  ToggleOpenWindowDetected = "ToggleOpenWindowDetected",
  ToggleReconnect = "ToggleReconnect",
  FactoryReset = "RequestFactoryReset",
}

export const actionCreator = {
  createNew:
    (
      workspace: number,
      request: CreateUWG5NventRequest | null = null,
      amount: number = 1
    ): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub
        .send("CreateNewThermostat", workspace, amount, request)
        .catch(alert);
    },
  addExisting:
    (
      workspace: number,
      email: string,
      pageNumber: number
    ): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub
        .send("AddExisting", workspace, email, pageNumber)
        .catch(alert);
    },
  invoke:
    (id: string, action: UWG5NventAction): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send(action, id).catch(alert);
    },
  updateSettings:
    (id: string, settings: UWG5NventSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateSettings", id, settings).catch(alert);
    },
  setSetpoint:
    (id: string, setpoint: number): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetSetpoint", id, setpoint).catch(alert);
    },
  setAutoMode:
    (id: string): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetAutoMode", id).catch(alert);
    },
  setManualMode:
    (id: string, setpoint: number): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetManualMode", id, setpoint).catch(alert);
    },
  setHoldMode:
    (
      id: string,
      setpoint: number,
      endTime: Date | null,
      isPermanent: boolean | null
    ): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub
        .send("SetHoldMode", id, setpoint, endTime, isPermanent)
        .catch(alert);
    },
  cancelHoldMode:
    (id: string): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("CancelHoldMode", id).catch(alert);
    },
  setServerUrl:
    (serverUrl: string): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetServerUrl", serverUrl).catch(alert);
    },
  setErrors:
    (id: string, errors: Errors): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetErrors", id, errors).catch(alert);
    },
  setStandbyState:
    (id: string, isInStandby: boolean): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetStandbyState", id, isInStandby).catch(alert);
    },
  setCurrentScheduleEvent:
    (id: string, day: number, eventId: number): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub
        .send("SetCurrentScheduleEvent", id, day, eventId)
        .catch(alert);
    },
  setCurrentTemp:
    (id: string, newTemp: number): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SetCurrentTemp", id, newTemp).catch(alert);
    },
  fetchLoadableThermostats:
    (): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
      uwg5NventHub.send("FetchLoadableThermostats").catch(alert);
    },
  loadThermostats:
    (ids: string[]): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("LoadThermostats", ids).catch(alert);
    },
  deleteThermostats:
    (ids: string[]): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("DeleteThermostats", ids).catch(alert);
    },
  updateDeviceInfo:
    (id: string, deviceInfo: DeviceInfo): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      const obj = {
        ...deviceInfo,
        mcuUuid: toBase64(deviceInfo.mcuUuid),
        protocolVersion: toBase64(deviceInfo.protocolVersion),
      };
      uwg5NventHub.send("UpdateDeviceInfo", id, obj).catch(alert);
    },
  updateDisplaySettings:
    (id: string, settings: DisplaySettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateDisplaySettings", id, settings).catch(alert);
    },
  updateWifiSettings:
    (id: string, settings: WifiSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateWifiSettings", id, settings).catch(alert);
    },
  updateRegulationSettings:
    (id: string, settings: RegulationSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateRegulationSettings", id, settings).catch(alert);
    },
  updateHeatingSettings:
    (id: string, settings: HeatingSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateHeatingSettings", id, settings).catch(alert);
    },
  updateFloorSensorSettings:
    (id: string, settings: FloorSensorSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateFloorSensorSettings", id, settings).catch(alert);
    },
  updateAwayModeSettings:
    (id: string, settings: AwayModeSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateAwayModeSettings", id, settings).catch(alert);
    },
  updateDateTimeSettings:
    (id: string, settings: DateTimeSettings): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateDateTimeSettings", id, settings).catch(alert);
    },
  updateWarrantyInfo:
    (id: string, info: WarrantyInfo): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("UpdateWarrantyInfo", id, info).catch(alert);
    },
  searchDistributors:
    (query: string): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SearchDistributors", query).catch(alert);
    },
  searchUsers:
    (query: string): AppThunkAction<KnownAction> =>
    (_dispatch, _getState) => {
      uwg5NventHub.send("SearchUsers", query).catch(alert);
    },
};
